import React, {useContext, useEffect, useRef, useState} from 'react';
import {SearchInputModel, SharedSearchBar, SuggestItem} from '@frontend/shared';
import {AppContext} from '@app/context/context';
import {getAdminSearchDetailUrl, getAdminSearchUrl, hasAnyRole} from '@utils';
import {useNavigate, useNavigationType, useSearchParams} from 'react-router-dom';
import {CApi as adminGwApi} from '@services/api';
import {useSearchHistory} from '@features/search/hooks';
import {SearchRequest} from '@features/search/interfaces';

export const SearchInput = () => {
    const {state, emit} = useContext(AppContext);
    const user = state.user;
    const clientSelected = state.selectedClient?.client;
    const isDisabled = !(state.selectedClient?.isSelected ?? false);
    const showAsyncSwitch = hasAnyRole(user, ['superadmin', 'supervisor']);

    const navigationType = useNavigationType();
    const navigate = useNavigate();

    const [searchParams] = useSearchParams();
    const [searchHistory, setSearchHistory] = useSearchHistory();

    const useAnalogs = !(searchParams.get('useAnalogs') === 'false');

    const [searchText, setSearchText] = useState<SearchInputModel>({
        originalValue: searchParams.get('text') || '',
        cleanedValue: searchParams.get('text') || '',
        needToSuggestBrands: false
    });
    const [searchBrand, setSearchBrand] = useState(
        decodeURIComponent(searchParams.get('brand') || '')
    );
    const [suggestItems, setSuggestItems] = useState<SuggestItem[]>([]);

    const [historySelectNonce, setHistorySelectNonce] = useState(0);

    const urlText = decodeURIComponent(searchParams.get('text') || '');
    const urlBrand = decodeURIComponent(searchParams.get('brand') || '');
    const urlClient = decodeURIComponent(searchParams.get('client') || '');
    const urlName = decodeURIComponent(searchParams.get('name') || '');

    // Метка перехода именно с /reports
    const fromReports = searchParams.get('from') === 'reports';
    // Deep link: клиент + текст + бренд
    const isDeepLink = Boolean(urlClient && urlText && urlBrand);
    // Автозапуск ТОЛЬКО если это переход с reports
    const shouldAutoSearch = fromReports && isDeepLink;
    const [awaitingDeepLinkClient, setAwaitingDeepLinkClient] = useState(shouldAutoSearch);
    const didAutoSearch = useRef(false);

    // добавляем nonce, чтобы был перезапрос даже на тот же роут
    const withNonce = (url: string) => {
        const sep = url.includes('?') ? '&' : '?';
        return `${url}${sep}nonce=${Date.now()}`;
    };

    useEffect(() => {
        if (!urlText) {
            setSuggestItems([]);
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                const items = await fetchClientSuggestions(urlText, clientSelected || "");
                if (!cancelled) setSuggestItems(items);
            } catch {
                if (!cancelled) setSuggestItems([]);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [urlText]);

    useEffect(() => {
        if (!state.selectedClient?.isSelected) {
            if (awaitingDeepLinkClient) return;

            setSearchText({
                originalValue: '',
                cleanedValue: '',
                needToSuggestBrands: false
            });
            setSearchBrand('');
            setSuggestItems([]);
        }
    }, [state.selectedClient?.isSelected, useAnalogs, awaitingDeepLinkClient]);

    // Автозапуск поиска при переходе из /reports
    useEffect(() => {
        if (!shouldAutoSearch) return;
        if (didAutoSearch.current) return;
        if (!state.selectedClient?.isSelected) return;

        // ждём пока выбранный клиент совпадёт с urlClient
        if (!clientSelected || clientSelected !== urlClient) return;
        if (!urlText || !urlBrand) return;

        // подставляем значения в инпут поиска детали
        setSearchText({
            originalValue: urlText,
            cleanedValue: urlText,
            needToSuggestBrands: false
        });
        setSearchBrand(urlBrand);

        didAutoSearch.current = true;
        setAwaitingDeepLinkClient(false);

        // делаем replace на URL БЕЗ from=reports, чтобы эффект "выключился"
        const rawTarget = getAdminSearchUrl(urlText, urlBrand, useAnalogs, clientSelected);

        // на случай если getAdminSearchUrl вдруг тянет лишние параметры — выкидываем from
        const u = new URL(rawTarget, 'http://local');
        u.searchParams.delete('from');

        navigate(withNonce(u.pathname + u.search), {replace: true});
    }, [
        shouldAutoSearch,
        state.selectedClient?.isSelected,
        clientSelected,
        urlClient,
        urlText,
        urlBrand,
        urlName,
        useAnalogs,
        navigate
    ]);

    useEffect(() => {
        if (navigationType === 'POP') {
            const navEntries = performance.getEntriesByType(
                'navigation'
            ) as PerformanceNavigationTiming[];
            const lastEntry = navEntries[navEntries.length - 1];
            if (lastEntry?.type === 'reload') {
                setSearchText({
                    originalValue: '',
                    cleanedValue: '',
                    needToSuggestBrands: false
                });
                return;
            }
        }

        const brand = decodeURIComponent(searchParams.get('brand') || '');
        const text = decodeURIComponent(searchParams.get('text') || '');
        const useAnalogs = !(searchParams.get('useAnalogs') === 'false');

        emit('updateSearchInputMainHeader', {
            searchInputMainHeader: {
                text,
                brand,
                useAnalogs
            }
        });
    }, [searchParams, navigationType, navigate]);

    async function fetchClientSuggestions(article: string, client: string): Promise<SuggestItem[]> {
        const r = await adminGwApi().suggestBrand.suggestBrandList({article, client});

        return r.data.items;
    }

    const handleInputChange = (value: SearchInputModel) => {
        setSearchBrand('');
        setSearchText(value);

        emit('updateSearchInputMainHeader', {
            searchInputMainHeader: {
                text: value.cleanedValue,
                brand: '',
                useAnalogs
            }
        });

        if (searchText.cleanedValue === value.cleanedValue) return;
        setSuggestItems([]);
    };

    function handleSearchClick() {
        if (isDisabled) {
            emit('clientSelectionError', {isError: true});
            return;
        }

        if (!searchText.cleanedValue) return;

        if (searchBrand) {
            if (suggestItems.length === 1) {
                // если suggestItems[0] не совпал/пуст — fallback на searchBrand
                const brand = suggestItems[0]?.name || searchBrand;

                navigate(
                    withNonce(
                        getAdminSearchUrl(
                            searchText.cleanedValue,
                            brand,
                            useAnalogs,
                            clientSelected
                        )
                    )
                );
                return;
            }

            navigate(
                withNonce(
                    getAdminSearchDetailUrl(searchText.cleanedValue, useAnalogs, clientSelected)
                )
            );
            return;
        }

        if (suggestItems.length === 1) {
            navigate(
                withNonce(
                    getAdminSearchUrl(
                        searchText.cleanedValue,
                        suggestItems[0].name,
                        useAnalogs,
                        clientSelected
                    )
                )
            );
            return;
        }

        navigate(
            withNonce(getAdminSearchDetailUrl(searchText.cleanedValue, useAnalogs, clientSelected))
        );
    }

    function handleBrandSelected(brand: SuggestItem) {
        if (searchText.cleanedValue) {
            const newSearchItem: SearchRequest = {
                article: searchText.cleanedValue,
                brand: brand.name,
                articleName: brand.articleName || searchText.originalValue || '',
                client: clientSelected || ''
            };

            setSearchHistory((prevHistory: SearchRequest[]) => {
                const filteredHistory = prevHistory.filter(
                    (item) =>
                        !(
                            item.article === newSearchItem.article &&
                            item.brand === newSearchItem.brand &&
                            item.client === newSearchItem.client
                        )
                );

                return [newSearchItem, ...filteredHistory].slice(0, 10);
            });

            navigate(
                getAdminSearchUrl(searchText.cleanedValue, brand.name, useAnalogs, clientSelected)
            );
            setSearchBrand(brand.name);
        }
    }

    function handleSearchHistorySelected(searchHistory: SearchRequest) {
        setSearchHistory((prevHistory: SearchRequest[]) => {
            const filteredHistory = prevHistory.filter(
                (item) =>
                    !(
                        item.article === searchHistory.article &&
                        item.brand === searchHistory.brand &&
                        item.client === searchHistory.client
                    )
            );
            return [searchHistory, ...filteredHistory];
        });

        setSuggestItems([]); // чтобы не было старых подсказок
        setSearchText({
            originalValue: searchHistory.article,
            cleanedValue: searchHistory.article,
            needToSuggestBrands: true
        });
        setSearchBrand(searchHistory.brand);

        // форсим ремаунт SharedSearchBar
        setHistorySelectNonce((x) => x + 1);

        navigate(
            getAdminSearchUrl(
                searchHistory.article,
                searchHistory.brand,
                useAnalogs,
                clientSelected
            )
        );
    }

    // Функция для обновления URL при изменении useAnalogs
    const handleUseAnalogsChange = (value: boolean) => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('useAnalogs', String(value));
        navigate(`?${newSearchParams.toString()}`, {replace: true});
    };

    return (
        <div>
            <SharedSearchBar
                key={historySelectNonce}
                size="s"
                mode="admin"
                disabledTextInput={isDisabled}
                showAsyncSwitch={showAsyncSwitch}
                searchText={searchText}
                searchBrand={searchBrand}
                useAnalogs={useAnalogs}
                onSearchClick={handleSearchClick}
                onInputChange={handleInputChange}
                onBrandSelected={handleBrandSelected}
                onBrandSuggestionsChanged={(items) => setSuggestItems(items)}
                onUseAnalogsChanged={handleUseAnalogsChange}
                fetchBrandSuggestions={fetchClientSuggestions}
                searchHistory={searchHistory}
                onSearchHistorySelected={handleSearchHistorySelected}
            />
        </div>
    );
};
